import './i18n/autoTranslate';
import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  Copy,
  Download,
  FileText,
  Loader2,
  Rocket,
  SearchCheck,
  Sparkles,
  Target,
  UploadCloud,
  Wand2,
  XCircle,
  Zap,
} from "lucide-react";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:5001";

function getAccessToken() {
  return localStorage.getItem("access_token") || localStorage.getItem("token") || "";
}

async function fetchJson(path, { method = "GET", body, isForm = false } = {}) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(isForm ? {} : { "Content-Type": "application/json" }),
    },
    body: isForm ? body : body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
  }
  return data;
}

function clamp(n, a = 0, b = 100) {
  const x = Number(n);
  if (!Number.isFinite(x)) return a;
  return Math.max(a, Math.min(b, x));
}

function SoftCard({ className = "", children }) {
  return (
    <div className={`rounded-2xl bg-white/[0.04] ring-1 ring-white/10 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl ${className}`}>
      {children}
    </div>
  );
}

function ProgressBar({ value }) {
  const pct = clamp(value);
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
      <div className="h-full rounded-full bg-gradient-to-r from-amber-300 via-emerald-300 to-sky-300 transition-all duration-700" style={{ width: `${pct}%` }} />
    </div>
  );
}

function StatPill({ label, value, hint, icon: Icon, accent = "from-amber-400/25 to-white/5" }) {
  return (
    <SoftCard className="p-4">
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-b ${accent} ring-1 ring-white/10`}>
          <Icon className="h-5 w-5 text-white/85" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-white/60">{label}</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-semibold text-white tabular-nums">{value}</div>
            {hint ? <div className="truncate text-xs text-white/50">{hint}</div> : null}
          </div>
        </div>
      </div>
    </SoftCard>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-zinc-950/90 px-3 py-2 shadow-xl ring-1 ring-white/10 backdrop-blur-xl">
      {label ? <div className="mb-1 text-xs text-white/60">{label}</div> : null}
      {payload.map((p, idx) => (
        <div key={idx} className="flex items-center gap-2 text-sm text-white">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill || p.stroke || "#fff" }} />
          <span className="text-white/65">{p.name}:</span>
          <span className="font-medium tabular-nums">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function copyText(text) {
  navigator.clipboard?.writeText(text || "");
}

function downloadText(filename, text) {
  const blob = new Blob([text || ""], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function isTechnicalRole(role = "", jd = "") {
  const hay = `${role} ${jd}`.toLowerCase();
  return /(developer|engineer|software|frontend|backend|full stack|devops|data scientist|machine learning|react|node|python|java|typescript|cloud|security|qa|tester)/i.test(hay);
}

function buildInterviewConfig(result, jobDescription) {
  const role = result?.targetRole || result?.jobTitle || "Target Role";
  const technical = isTechnicalRole(role, jobDescription);
  return {
    selectedType: technical ? "technical" : "behavioral",
    selectedIndustry: result?.industry || "",
    selectedRole: role,
    selectedCompanyType: "",
    estimatedQuestions: 8,
    personality: {
      selectedPersona: {},
      tone: 3,
      difficulty: 4,
      pace: 3,
      pressure: 3,
      accent: "american",
    },
    selectedCategories: technical ? ["technical", "behavioral"] : ["behavioral"],
    selectedCompanies: [],
    interviewType: technical ? "technical" : "behavioral",
    customQuestions: (result?.interviewQuestions || []).slice(0, 10),
    enableFollowUps: true,
    surpriseQuestions: true,
    starMethodEnforcement: true,
    followUpIntensity: 3,
    adaptiveDifficulty: true,
    selectedFormat: "phone",
    duration: 30,
    elevenlabsVoice: null,
  };
}

export default function SalaryBoostPage() {
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeText, setResumeText] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [coverLetter, setCoverLetter] = useState("");
  const [rewriting, setRewriting] = useState(false);

  const score = clamp(result?.atsScore ?? 0);
  const matchData = useMemo(() => {
    if (!result) return [];
    return [
      { name: "ATS", value: clamp(result.atsScore) },
      { name: "Keywords", value: clamp(result.keywordScore) },
      { name: "Impact", value: clamp(result.impactScore) },
      { name: "Clarity", value: clamp(result.clarityScore) },
      { name: "Format", value: clamp(result.formatScore) },
    ];
  }, [result]);

  const keywordBars = useMemo(() => {
    const found = result?.matchedKeywords?.length || 0;
    const missing = result?.missingKeywords?.length || 0;
    const total = Math.max(found + missing, 1);
    return [
      { name: "Matched", value: Math.round((found / total) * 100) },
      { name: "Missing", value: Math.round((missing / total) * 100) },
    ];
  }, [result]);

  async function analyzeResume(e) {
    e?.preventDefault?.();
    if (!resumeFile && !resumeText.trim()) {
      setError("Upload a resume PDF or paste resume text first.");
      return;
    }
    if (!jobDescription.trim()) {
      setError("Paste the job description so the AI can optimize for a real target job.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setCoverLetter("");

    try {
      const form = new FormData();
      if (resumeFile) form.append("resume", resumeFile);
      form.append("resumeText", resumeText);
      form.append("jobDescription", jobDescription);
      form.append("targetRole", targetRole);

      const data = await fetchJson("/api/resume/ats/analyze", {
        method: "POST",
        body: form,
        isForm: true,
      });
      setResult(data?.analysis || data);
    } catch (err) {
      setError(err?.message || "Failed to analyze resume.");
    } finally {
      setLoading(false);
    }
  }

  async function generateCoverLetter() {
    if (!result) return;
    setRewriting(true);
    setError("");
    try {
      const data = await fetchJson("/api/resume/ats/cover-letter", {
        method: "POST",
        body: {
          resumeText: result.resumeText || resumeText,
          jobDescription,
          targetRole: result.targetRole || targetRole,
          analysis: result,
        },
      });
      setCoverLetter(data?.coverLetter || "");
    } catch (err) {
      setError(err?.message || "Failed to generate cover letter.");
    } finally {
      setRewriting(false);
    }
  }

  function startInterviewFromResume() {
    if (!result) return;
    const config = buildInterviewConfig(result, jobDescription);
    localStorage.setItem("finalInterviewConfiguration", JSON.stringify(config));
    const technical = config.interviewType === "technical";
    window.location.href = technical ? "/tech-interview" : "/interview-setup-configuration";
  }

  return (
    <div className="min-h-screen bg-[#05070c] text-white">
      

      <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs text-white/70 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-amber-200" />
              SalaryBoost Engine
            </div>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
              AI Resume & ATS Optimization
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-white/60">
              Upload a resume, paste a real job description, get an ATS score, missing keywords, rewritten bullets, and one-click interview practice for that exact role.
            </p>
          </div>

          {result ? (
            <button
              onClick={startInterviewFromResume}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-300 to-emerald-300 px-5 py-3 text-sm font-semibold text-zinc-950 shadow-lg shadow-amber-500/10 transition hover:scale-[1.01]"
            >
              Practice this job interview <ArrowRight className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <form onSubmit={analyzeResume} className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <SoftCard className="p-5 lg:col-span-1">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/15 ring-1 ring-amber-300/20">
                <UploadCloud className="h-5 w-5 text-amber-100" />
              </div>
              <div>
                <div className="font-semibold text-white">Resume input</div>
                <div className="text-xs text-white/55">PDF upload or raw text</div>
              </div>
            </div>

            <label className="mt-5 flex min-h-[150px] cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/[0.03] p-5 text-center hover:bg-white/[0.05]">
              <FileText className="h-8 w-8 text-white/60" />
              <div className="mt-3 text-sm font-medium text-white">
                {resumeFile ? resumeFile.name : "Drop / choose resume PDF"}
              </div>
              <div className="mt-1 text-xs text-white/45">Max 15MB. PDF works best.</div>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
              />
            </label>

            <textarea
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Or paste resume text here..."
              className="mt-4 min-h-[170px] w-full resize-none rounded-2xl bg-white/[0.03] p-4 text-sm text-white outline-none ring-1 ring-white/10 placeholder:text-white/35 focus:ring-amber-300/30"
            />
          </SoftCard>

          <SoftCard className="p-5 lg:col-span-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-400/15 ring-1 ring-sky-300/20">
                <BriefcaseBusiness className="h-5 w-5 text-sky-100" />
              </div>
              <div>
                <div className="font-semibold text-white">Target job</div>
                <div className="text-xs text-white/55">The AI optimizes against this exact opportunity</div>
              </div>
            </div>

            <input
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              placeholder="Target role e.g. Full Stack Developer, Sales Manager..."
              className="mt-5 w-full rounded-2xl bg-white/[0.03] px-4 py-3 text-sm text-white outline-none ring-1 ring-white/10 placeholder:text-white/35 focus:ring-sky-300/30"
            />

            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full job description here..."
              className="mt-4 min-h-[300px] w-full resize-none rounded-2xl bg-white/[0.03] p-4 text-sm text-white outline-none ring-1 ring-white/10 placeholder:text-white/35 focus:ring-sky-300/30"
            />

            {error ? (
              <div className="mt-4 rounded-2xl bg-red-500/10 p-3 text-sm text-red-200 ring-1 ring-red-500/20">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-300 via-emerald-300 to-sky-300 px-5 py-3 text-sm font-semibold text-zinc-950 shadow-lg shadow-emerald-500/10 transition hover:scale-[1.005] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <SearchCheck className="h-4 w-4" />}
              {loading ? "Analyzing resume..." : "Analyze ATS Match"}
            </button>
          </SoftCard>
        </form>

        {result ? (
          <>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <StatPill label="ATS Score" value={`${Math.round(score)}%`} hint={score >= 80 ? "Strong match" : "Needs boost"} icon={Target} accent="from-amber-400/25 to-white/5" />
              <StatPill label="Keywords" value={`${Math.round(clamp(result.keywordScore))}%`} hint={`${result.missingKeywords?.length || 0} missing`} icon={Zap} accent="from-emerald-400/25 to-white/5" />
              <StatPill label="Impact" value={`${Math.round(clamp(result.impactScore))}%`} hint="Metrics + action verbs" icon={Rocket} accent="from-sky-400/25 to-white/5" />
              <StatPill label="Clarity" value={`${Math.round(clamp(result.clarityScore))}%`} hint="Recruiter readability" icon={FileText} accent="from-violet-400/25 to-white/5" />
              <StatPill label="Format" value={`${Math.round(clamp(result.formatScore))}%`} hint="ATS structure" icon={CheckCircle2} accent="from-fuchsia-400/25 to-white/5" />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <SoftCard className="p-5 lg:col-span-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white">Optimization breakdown</div>
                    <div className="mt-1 text-sm text-white/55">Where the resume wins and where it loses ATS match.</div>
                  </div>
                  <div className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/60 ring-1 ring-white/10">Live AI report</div>
                </div>
                <div className="mt-5 h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={matchData} margin={{ left: 8, right: 8, top: 10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="salaryBoostFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="rgba(251,191,36,0.35)" />
                          <stop offset="95%" stopColor="rgba(56,189,248,0.02)" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 12 }} tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="value" name="Score" stroke="rgba(251,191,36,0.95)" strokeWidth={2} fill="url(#salaryBoostFill)" isAnimationActive={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </SoftCard>

              <SoftCard className="p-5">
                <div className="font-semibold text-white">Keyword match</div>
                <div className="mt-1 text-sm text-white/55">Matched vs missing job keywords</div>
                <div className="mt-5 h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={keywordBars} margin={{ left: 8, right: 8, top: 5, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 12 }} tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="value" name="%" radius={[8, 8, 0, 0]} isAnimationActive={false}>
                        <Cell fill="rgba(52,211,153,0.8)" />
                        <Cell fill="rgba(251,191,36,0.8)" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3">
                  <ProgressBar value={result.keywordScore} />
                </div>
              </SoftCard>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <SoftCard className="p-5">
                <div className="flex items-center gap-2 font-semibold text-white"><XCircle className="h-4 w-4 text-amber-200" /> Missing keywords</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(result.missingKeywords || []).slice(0, 40).map((k, i) => (
                    <span key={i} className="rounded-full bg-amber-500/15 px-2 py-1 text-xs text-amber-100 ring-1 ring-amber-500/20">{k}</span>
                  ))}
                </div>
              </SoftCard>

              <SoftCard className="p-5">
                <div className="flex items-center gap-2 font-semibold text-white"><CheckCircle2 className="h-4 w-4 text-emerald-200" /> Matched keywords</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(result.matchedKeywords || []).slice(0, 40).map((k, i) => (
                    <span key={i} className="rounded-full bg-emerald-500/15 px-2 py-1 text-xs text-emerald-100 ring-1 ring-emerald-500/20">{k}</span>
                  ))}
                </div>
              </SoftCard>

              <SoftCard className="p-5">
                <div className="flex items-center gap-2 font-semibold text-white"><Sparkles className="h-4 w-4 text-sky-200" /> AI summary</div>
                <p className="mt-4 text-sm leading-relaxed text-white/70">{result.summary}</p>
              </SoftCard>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <SoftCard className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white">Bullet rewrites</div>
                    <div className="mt-1 text-sm text-white/55">Copy these into your resume to add measurable impact.</div>
                  </div>
                  <button onClick={() => copyText((result.rewrittenBullets || []).map((b) => `Before: ${b.before}\nAfter: ${b.after}`).join("\n\n"))} className="rounded-xl bg-white/5 p-2 ring-1 ring-white/10 hover:bg-white/10">
                    <Copy className="h-4 w-4 text-white/70" />
                  </button>
                </div>
                <div className="mt-4 space-y-3">
                  {(result.rewrittenBullets || []).map((b, i) => (
                    <div key={i} className="rounded-2xl bg-white/[0.03] p-4 ring-1 ring-white/10">
                      <div className="text-xs uppercase tracking-wide text-white/40">Before</div>
                      <p className="mt-1 text-sm text-white/55">{b.before}</p>
                      <div className="mt-3 text-xs uppercase tracking-wide text-emerald-200/80">After</div>
                      <p className="mt-1 text-sm leading-relaxed text-white/80">{b.after}</p>
                    </div>
                  ))}
                </div>
              </SoftCard>

              <SoftCard className="p-5">
                <div className="font-semibold text-white">Priority fixes</div>
                <div className="mt-4 space-y-3">
                  {(result.priorityFixes || []).map((x, i) => (
                    <div key={i} className="rounded-2xl bg-white/[0.03] p-4 ring-1 ring-white/10">
                      <div className="flex items-start gap-3">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 text-xs font-semibold text-amber-100 ring-1 ring-amber-300/20">{i + 1}</div>
                        <div>
                          <div className="text-sm font-medium text-white">{x.title || x.area}</div>
                          <div className="mt-1 text-sm leading-relaxed text-white/60">{x.action || x.description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </SoftCard>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <SoftCard className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-white">Cover letter generator</div>
                    <div className="mt-1 text-sm text-white/55">Generate a targeted letter using the same resume + job match.</div>
                  </div>
                  <button onClick={generateCoverLetter} disabled={rewriting} className="inline-flex items-center gap-2 rounded-2xl bg-white/5 px-4 py-2 text-sm text-white/80 ring-1 ring-white/10 hover:bg-white/10 disabled:opacity-60">
                    {rewriting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    Generate
                  </button>
                </div>
                <textarea value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} placeholder="Generated cover letter will appear here..." className="mt-4 min-h-[300px] w-full resize-none rounded-2xl bg-white/[0.03] p-4 text-sm leading-relaxed text-white outline-none ring-1 ring-white/10 placeholder:text-white/35" />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => copyText(coverLetter)} className="inline-flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2 text-xs text-white/70 ring-1 ring-white/10 hover:bg-white/10"><Copy className="h-3.5 w-3.5" /> Copy</button>
                  <button onClick={() => downloadText("cover-letter.txt", coverLetter)} className="inline-flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2 text-xs text-white/70 ring-1 ring-white/10 hover:bg-white/10"><Download className="h-3.5 w-3.5" /> Download</button>
                </div>
              </SoftCard>

              <SoftCard className="p-5">
                <div className="font-semibold text-white">Interview questions from this job</div>
                <div className="mt-1 text-sm text-white/55">These are saved into finalInterviewConfiguration when you start practice.</div>
                <div className="mt-4 space-y-3">
                  {(result.interviewQuestions || []).slice(0, 8).map((q, i) => (
                    <div key={i} className="rounded-2xl bg-white/[0.03] p-4 text-sm text-white/75 ring-1 ring-white/10">
                      <span className="mr-2 text-white/35">#{i + 1}</span>{q}
                    </div>
                  ))}
                </div>
              </SoftCard>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
